import React from "react";
import classNames from "classnames";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { useFillAccount } from "@planzone/view-models";

import { Button } from "@common/button";
import { TextInput } from "@common/textInput";
import { Error } from "./error";
import { Props } from "./types";
import styles from "./styles.scss";

function FillAccount({ token }: Props) {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState({
    lastName: "",
    firstName: "",
    password: "",
  });
  const { isRequestPending, error, fillAccount } = useFillAccount();

  React.useEffect(() => {
    try {
      const data = jwtDecode<{ lastName: string; firstName: string }>(token);

      setFormData((formData) => ({
        ...formData,
        firstName: data.firstName,
        lastName: data.lastName,
      }));
    } catch (e) {}
  }, [token]);

  return error.alreadyConfirmed ? (
    <div className={styles.redirection}>
      <div className={styles.main}>
        <h4 className={styles.title}>{t("fillAccount.redirection.title")}</h4>
        <p className={styles.message}>{t("fillAccount.redirection.message")}</p>
        <a href="/" className={styles.redirectionButton}>
          {t("fillAccount.redirection.redirect")}
        </a>
      </div>
    </div>
  ) : (
    <form
      className={styles.fillAccount}
      onSubmit={(e) => {
        e.preventDefault();
        !isRequestPending && fillAccount({ token, ...formData });
      }}
    >
      <p className={styles.message}>
        {t("fillAccount.message", {
          fullName: `${formData.firstName} ${formData.lastName}`,
        })}
      </p>
      <div className={styles.group}>
        <h3 className={styles.label}>{t("fillAccount.password.label")}</h3>
        <TextInput
          value={formData.password}
          className={classNames(styles.input, {
            [styles.invalid]:
              error.invalidPassword.isTooShort ||
              error.invalidPassword.doesNotHaveUpper ||
              error.invalidPassword.doesNotHaveNumberOrSpecialChar,
          })}
          placeholder={t("fillAccount.password.placeholder")}
          isInvalid={
            error.invalidPassword.isTooShort ||
            error.invalidPassword.doesNotHaveUpper ||
            error.invalidPassword.doesNotHaveNumberOrSpecialChar
          }
          input={(password) => setFormData({ ...formData, password })}
          isSecured
        />
        <div className={styles.error}>
          <Error error={error.invalidPassword} />
        </div>
      </div>
      <div className={styles.submitButton}>
        <Button type="submit" status={isRequestPending ? "pending" : "none"}>
          {t("fillAccount.submit")}
        </Button>
      </div>
    </form>
  );
}

export { FillAccount };
